import APIService from "./APIService";

/**
 * Service for handling Stripe Payment API operations
 *
 * This service interfaces with the Spring Boot PaymentController
 * Expected response format:
 * {
 *   "success": true/false,
 *   "message": "Descriptive message",
 *   "data": { ... }
 * }
 */
const PaymentService = {
  /**
   * Create a setup intent for adding payment method
   * @param {string} email - User email
   * @param {string} name - User name
   * @returns {Promise} Promise that resolves with setup intent data
   */
  createSetupIntent: async (email, name) => {
    try {
      console.log("[PaymentService] Creating setup intent for:", email);
      const response = await APIService.post(
        "/api/v1/app/payments/stripe/setup-intent",
        {
          email,
          name,
        },
      );
      console.log("[PaymentService] Create setup intent response:", response);
      return response;
    } catch (error) {
      console.error("[PaymentService] Error creating setup intent:", error);
      throw error;
    }
  },

  /**
   * Save payment method after successful setup
   * @param {string} paymentMethodId - Stripe payment method ID
   * @param {string} customerId - Stripe customer ID
   * @param {number} accountId - Account ID
   * @param {boolean} setAsDefault - Whether to set as default payment method
   * @returns {Promise} Promise that resolves with saved payment method
   */
  savePaymentMethod: async (
    paymentMethodId,
    customerId,
    accountId,
    setAsDefault = false,
  ) => {
    try {
      console.log("[PaymentService] Saving payment method:", paymentMethodId);
      const response = await APIService.post(
        "/api/v1/app/payments/stripe/payment-methods",
        {
          paymentMethodId,
          customerId,
          accountId,
          setAsDefault,
        },
      );
      console.log("[PaymentService] Save payment method response:", response);
      return response;
    } catch (error) {
      console.error("[PaymentService] Error saving payment method:", error);
      throw error;
    }
  },

  /**
   * Get all payment methods for authenticated user
   * @returns {Promise} Promise that resolves with array of payment methods
   */
  getPaymentMethods: async () => {
    try {
      console.log("[PaymentService] Getting payment methods");
      const response = await APIService.get(
        "/api/v1/app/payments/stripe/payment-methods",
      );
      console.log("[PaymentService] Get payment methods response:", response);
      return response;
    } catch (error) {
      console.error("[PaymentService] Error fetching payment methods:", error);
      throw error;
    }
  },

  /**
   * Delete a payment method
   * @param {number} paymentMethodId - Payment method ID to delete
   * @returns {Promise} Promise that resolves with deletion confirmation
   */
  deletePaymentMethod: async (paymentMethodId) => {
    try {
      console.log("[PaymentService] Deleting payment method:", paymentMethodId);
      const response = await APIService.delete(
        `/api/v1/app/payments/stripe/payment-methods/${paymentMethodId}`,
      );
      console.log("[PaymentService] Delete payment method response:", response);
      return response;
    } catch (error) {
      console.error("[PaymentService] Error deleting payment method:", error);
      throw error;
    }
  },

  /**
   * Set default payment method
   * @param {number} paymentMethodId - Payment method ID to set as default
   * @returns {Promise} Promise that resolves with update confirmation
   */
  setDefaultPaymentMethod: async (paymentMethodId) => {
    try {
      console.log(
        "[PaymentService] Setting default payment method:",
        paymentMethodId,
      );
      const response = await APIService.put(
        `/api/v1/app/payments/stripe/payment-methods/${paymentMethodId}/default`,
        {},
      );
      console.log(
        "[PaymentService] Set default payment method response:",
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[PaymentService] Error setting default payment method:",
        error,
      );
      throw error;
    }
  },

  /**
   * Create payment intent for one-time payment
   * @param {Object} paymentData - Payment data including amount, currency, etc.
   * @param {number} accountId - Account ID
   * @returns {Promise} Promise that resolves with payment intent
   */
  createPaymentIntent: async (paymentData, accountId) => {
    try {
      console.log("[PaymentService] Creating payment intent:", paymentData);
      const response = await APIService.post(
        `/api/v1/app/payments/stripe/create-payment-intent?accountId=${accountId}`,
        paymentData,
      );
      console.log("[PaymentService] Create payment intent response:", response);
      return response;
    } catch (error) {
      console.error("[PaymentService] Error creating payment intent:", error);
      throw error;
    }
  },

  /**
   * Process a payment
   * @param {Object} paymentData - Payment data
   * @param {number} accountId - Account ID
   * @returns {Promise} Promise that resolves with payment result
   */
  processPayment: async (paymentData, accountId) => {
    try {
      console.log("[PaymentService] Processing payment:", paymentData);
      const response = await APIService.post(
        `/api/v1/app/payments/stripe/process?accountId=${accountId}`,
        paymentData,
      );
      console.log("[PaymentService] Process payment response:", response);
      return response;
    } catch (error) {
      console.error("[PaymentService] Error processing payment:", error);
      throw error;
    }
  },

  /**
   * Get payment history for authenticated user
   * @returns {Promise} Promise that resolves with array of payments
   */
  getPaymentHistory: async () => {
    try {
      console.log("[PaymentService] Getting payment history");
      const response = await APIService.get(
        "/api/v1/app/payments/stripe/history",
      );
      console.log("[PaymentService] Get payment history response:", response);
      return response;
    } catch (error) {
      console.error("[PaymentService] Error fetching payment history:", error);
      throw error;
    }
  },

  /**
   * Reconcile billing history by fetching paid invoices from Stripe
   * and backfilling any missing local payment records.
   * @returns {Promise} Promise that resolves with { message, paymentsRecovered }
   */
  reconcilePayments: async () => {
    try {
      console.log("[PaymentService] Reconciling payments with Stripe");
      const response = await APIService.post(
        "/api/v1/app/payments/stripe/reconcile",
        {},
      );
      console.log("[PaymentService] Reconcile payments response:", response);
      return response;
    } catch (error) {
      console.error("[PaymentService] Error reconciling payments:", error);
      throw error;
    }
  },
};

export default PaymentService;
